/**
 * Anonymous visitor tracker.
 *
 * - Generates a random session id stored in localStorage (no PII).
 * - Buffers events and flushes them via fetch / sendBeacon to the
 *   `track-visitor` edge function.
 * - On unload, fires an `exit` event so the backend can later run AI
 *   inference on the trail.
 *
 * Privacy: no name, email, IP, or precise identifier is collected.
 */

const STORAGE_KEY = "vss_visitor_session";
const LAST_PATH_KEY = "vss_visitor_last_path";
const FLUSH_INTERVAL_MS = 15_000;
const MAX_BUFFER = 25;

type EventType =
  | "session_start"
  | "pageview"
  | "click"
  | "form_start"
  | "form_field"
  | "form_abandon"
  | "scroll_depth"
  | "conversion"
  | "exit"
  | "heartbeat";

interface VisitorEvent {
  event_type: EventType;
  path: string;
  metadata?: Record<string, unknown>;
  occurred_at: string;
}

interface SessionState {
  session_id: string;
  started_at: number;
  last_path: string | null;
  pageview_count: number;
  event_count: number;
  max_scroll_depth: number;
  converted: boolean;
}

let state: SessionState | null = null;
let buffer: VisitorEvent[] = [];
let flushTimer: number | null = null;
let lastPathLogged: string | null = null;
let lastActivityAt = Date.now();
let endpointUrl: string | null = null;
let publishableKey: string | null = null;

const safeLocal = (() => {
  try {
    if (typeof window === "undefined") return null;
    const t = "__vss_t";
    window.localStorage.setItem(t, "1");
    window.localStorage.removeItem(t);
    return window.localStorage;
  } catch {
    return null;
  }
})();

function genSessionId(): string {
  // Crypto-quality random id; 22-char base36
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function detectDevice(): string {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) return "mobile";
  if (/Tablet|iPad/i.test(ua)) return "tablet";
  return "desktop";
}

function loadOrInitSession(): SessionState {
  if (state) return state;
  let saved: Partial<SessionState> | null = null;
  if (safeLocal) {
    try {
      const raw = safeLocal.getItem(STORAGE_KEY);
      if (raw) saved = JSON.parse(raw);
    } catch {
      saved = null;
    }
  }
  // 30-min inactivity = new session
  const now = Date.now();
  const stale = saved?.started_at ? now - (saved.started_at as number) > 30 * 60_000 : true;
  if (saved?.session_id && !stale) {
    state = {
      session_id: saved.session_id,
      started_at: (saved.started_at as number) ?? now,
      last_path: saved.last_path ?? null,
      pageview_count: saved.pageview_count ?? 0,
      event_count: saved.event_count ?? 0,
      max_scroll_depth: saved.max_scroll_depth ?? 0,
      converted: !!saved.converted,
    };
  } else {
    state = {
      session_id: genSessionId(),
      started_at: now,
      last_path: null,
      pageview_count: 0,
      event_count: 0,
      max_scroll_depth: 0,
      converted: false,
    };
    queueEvent("session_start", {
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      device: detectDevice(),
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      entry_path: typeof location !== "undefined" ? location.pathname : null,
    });
  }
  persistState();
  return state;
}

function persistState() {
  if (!safeLocal || !state) return;
  try {
    safeLocal.setItem(STORAGE_KEY, JSON.stringify(state));
    if (state.last_path) safeLocal.setItem(LAST_PATH_KEY, state.last_path);
  } catch {
    // private mode etc — ignore
  }
}

function queueEvent(event_type: EventType, metadata?: Record<string, unknown>) {
  if (!state) return;
  const path = typeof location !== "undefined" ? location.pathname + location.search : "";
  buffer.push({
    event_type,
    path,
    metadata,
    occurred_at: new Date().toISOString(),
  });
  state.event_count += 1;
  lastActivityAt = Date.now();
  if (buffer.length >= MAX_BUFFER) {
    void flush();
  }
}

async function flush(useBeacon = false) {
  if (!state || buffer.length === 0 || !endpointUrl) return;
  const payload = {
    session_id: state.session_id,
    last_path: state.last_path,
    pageview_count: state.pageview_count,
    event_count: state.event_count,
    max_scroll_depth: state.max_scroll_depth,
    started_at: new Date(state.started_at).toISOString(),
    converted: state.converted,
    events: buffer.splice(0, buffer.length),
  };
  const body = JSON.stringify(payload);
  try {
    if (useBeacon && typeof navigator !== "undefined" && "sendBeacon" in navigator) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(endpointUrl, blob);
      return;
    }
    await fetch(endpointUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(publishableKey ? { Authorization: `Bearer ${publishableKey}`, apikey: publishableKey } : {}),
      },
      body,
      keepalive: true,
    });
  } catch {
    // swallow — tracking must never crash the app
  }
}

function setupScrollDepth() {
  if (typeof window === "undefined") return;
  const thresholds = [25, 50, 75, 100];
  const fired = new Set<number>();
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const winH = window.innerHeight;
      const docH = Math.max(doc.scrollHeight, doc.clientHeight);
      const pct = docH <= winH ? 100 : Math.min(100, Math.round(((scrollTop + winH) / docH) * 100));
      if (state && pct > state.max_scroll_depth) state.max_scroll_depth = pct;
      thresholds.forEach((t) => {
        if (pct >= t && !fired.has(t)) {
          fired.add(t);
          queueEvent("scroll_depth", { depth: t });
        }
      });
      ticking = false;
    });
  };
  // reset thresholds per route
  const resetForRoute = () => fired.clear();
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("vss:routechange", resetForRoute);
}

function setupClicks() {
  if (typeof document === "undefined") return;
  document.addEventListener(
    "click",
    (e) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const el = target.closest("a,button,[role=button]") as HTMLElement | null;
      if (!el) return;
      const text = (el.textContent || "").trim().slice(0, 80);
      const href = el instanceof HTMLAnchorElement ? el.getAttribute("href") : null;
      const isCta = /quote|call|text|book|measure|contact|estimate/i.test(text);
      queueEvent("click", {
        text,
        href,
        tag: el.tagName.toLowerCase(),
        cta: isCta || undefined,
      });
    },
    { capture: true, passive: true }
  );
}

function setupForms() {
  if (typeof document === "undefined") return;
  const startedForms = new WeakSet<HTMLFormElement>();
  const lastFieldByForm = new WeakMap<HTMLFormElement, string>();

  document.addEventListener(
    "focusin",
    (e) => {
      const t = e.target as HTMLElement;
      if (!t || !(t as HTMLInputElement).form) return;
      const form = (t as HTMLInputElement).form as HTMLFormElement;
      const name = (t as HTMLInputElement).name || (t as HTMLInputElement).id || t.tagName.toLowerCase();
      lastFieldByForm.set(form, name);
      if (!startedForms.has(form)) {
        startedForms.add(form);
        queueEvent("form_start", { form_id: form.id || form.name || "unknown", first_field: name });
      } else {
        queueEvent("form_field", { form_id: form.id || form.name || "unknown", field: name });
      }
    },
    { capture: true }
  );

  // detect abandon: page hide while form started but not converted
  window.addEventListener("pagehide", () => {
    if (!state || state.converted) return;
    document.querySelectorAll("form").forEach((form) => {
      if (startedForms.has(form as HTMLFormElement)) {
        queueEvent("form_abandon", {
          form_id: (form as HTMLFormElement).id || (form as HTMLFormElement).name || "unknown",
          last_field: lastFieldByForm.get(form as HTMLFormElement) || null,
        });
      }
    });
  });
}

function setupVisibility() {
  if (typeof document === "undefined") return;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      queueEvent("heartbeat", { reason: "hidden" });
      void flush(true);
    } else {
      lastActivityAt = Date.now();
    }
  });
  window.addEventListener("pagehide", () => {
    queueEvent("exit", {
      duration_ms: Date.now() - (state?.started_at ?? Date.now()),
      max_scroll: state?.max_scroll_depth ?? 0,
    });
    void flush(true);
  });
}

let _initialized = false;

export function initVisitorTracker(options: { endpoint: string; anonKey?: string }) {
  if (_initialized || typeof window === "undefined") return;
  _initialized = true;
  endpointUrl = options.endpoint;
  publishableKey = options.anonKey ?? null;
  loadOrInitSession();
  setupScrollDepth();
  setupClicks();
  setupForms();
  setupVisibility();
  if (flushTimer == null) {
    flushTimer = window.setInterval(() => void flush(), FLUSH_INTERVAL_MS);
  }
}

export function trackPageview(path: string) {
  const s = loadOrInitSession();
  if (lastPathLogged === path) return;
  lastPathLogged = path;
  s.pageview_count += 1;
  s.last_path = path;
  persistState();
  queueEvent("pageview", { path });
  // notify scroll-depth resetter
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("vss:routechange"));
}

export function trackConversion(type: string, metadata?: Record<string, unknown>) {
  const s = loadOrInitSession();
  s.converted = true;
  persistState();
  queueEvent("conversion", { type, ...(metadata || {}) });
  void flush();
}

export function getLastVisitedPath(): string | null {
  if (!safeLocal) return null;
  try {
    return safeLocal.getItem(LAST_PATH_KEY);
  } catch {
    return null;
  }
}

export function getSessionInfo() {
  return state ? { ...state } : null;
}
